import React, { useEffect, useState } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedButton from '../common/AnimatedButton';
import { FaRocket, FaLaptopCode, FaCode } from 'react-icons/fa';

const HeroSection = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const textOptions = [
    { text: 'Conception web', color: 'text-gradient-primary' },
    { text: 'Design moderne', color: 'text-gradient-purple' },
    { text: 'Performance optimale', color: 'text-gradient-accent' },
    { text: 'Sécurité Renforcée', color: 'text-gradient-red' },
    { text: 'E-commerce', color: 'text-gradient-orange' },
    { text: 'Maintenance', color: 'text-gradient-pink' }
  ];
  
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % textOptions.length);
    }, 3000); // Délai réduit pour une animation plus dynamique
    return () => clearInterval(interval);
  }, []);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2,
        delayChildren: 0.3
      } 
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.5
      } 
    }
  };
  
  const textVariants = {
    hidden: { y: 30, opacity: 0, scale: 0.9 },
    visible: { 
      y: 0, 
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.7,
        ease: "easeOut"
      } 
    },
    exit: {
      y: -30,
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.5,
        ease: "easeIn"
      }
    }
  };
  
  // Effet de lettres qui apparaissent une par une
  const letterVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3
      }
    })
  };

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 overflow-hidden">
      {/* Animated Background Elements */}
      {/* Background image overlay */}
      <div className="absolute inset-0 bg-cover bg-center z-0 opacity-20" 
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80')`,
          backgroundBlendMode: 'overlay'
        }}>
      </div>
      
      {/* Animated elements overlay */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating particles with animation plus fluide */}
        {[...Array(40)].map((_, i) => (
          <motion.div 
            key={i}
            className={`absolute rounded-full ${i % 4 === 0 ? 'bg-blue-300 h-3 w-3' : i % 4 === 1 ? 'bg-indigo-300 h-2 w-2' : i % 4 === 2 ? 'bg-purple-300 h-1.5 w-1.5' : 'bg-white h-1 w-1'}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.6 + 0.2,
              filter: 'blur(0.5px)'
            }}
            animate={{ 
              y: [0, Math.random() * 60 - 30, 0],
              x: [0, Math.random() * 60 - 30, 0],
              opacity: [Math.random() * 0.4 + 0.2, Math.random() * 0.7 + 0.3, Math.random() * 0.4 + 0.2]
            }}
            transition={{ 
              duration: Math.random() * 5 + 3,
              repeat: Infinity,
              repeatType: "loop",
              ease: "linear"
            }}
          />
        ))}
        
        {/* Large glowing orbs avec animation continue */}
        <motion.div 
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-400 blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1.1, 1.3, 1],
            opacity: [0.2, 0.4, 0.3, 0.35, 0.2],
            x: [0, 20, 10, 30, 0],
            y: [0, -10, -5, -15, 0]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            repeatType: "loop",
            ease: "linear"
          }}
        />
        <motion.div 
          className="absolute bottom-20 -left-20 w-80 h-80 rounded-full bg-indigo-500 blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1.1, 1.2, 1],
            opacity: [0.15, 0.3, 0.2, 0.25, 0.15],
            y: [0, -20, -10, -30, 0],
            x: [0, 10, 5, 15, 0]
          }}
          transition={{ 
            duration: 25,
            repeat: Infinity,
            repeatType: "loop",
            ease: "linear"
          }}
        />
        <motion.div 
          className="absolute top-1/3 left-1/4 w-64 h-64 rounded-full bg-purple-500 blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1.1, 1.25, 1],
            opacity: [0.1, 0.2, 0.15, 0.25, 0.1],
            x: [0, -15, -5, -20, 0],
            y: [0, 10, 5, 15, 0]
          }}
          transition={{ 
            duration: 18,
            repeat: Infinity,
            repeatType: "loop",
            ease: "linear"
          }}
        />
        
        {/* Code-like elements avec animation continue */}
        <motion.div 
          className="absolute top-1/4 right-10 text-white opacity-15 text-7xl"
          animate={{ 
            y: [0, -20, -10, -25, 0], 
            rotate: [0, 5, 2, 7, 0],
            scale: [1, 1.05, 1.02, 1.07, 1]
          }}
          transition={{ duration: 15, repeat: Infinity, repeatType: "loop", ease: "linear" }}
        >
          <FaCode />
        </motion.div>
        <motion.div 
          className="absolute bottom-1/4 left-10 text-white opacity-15 text-7xl"
          animate={{ 
            y: [0, 20, 10, 25, 0], 
            rotate: [0, -5, -2, -7, 0],
            scale: [1, 1.05, 1.02, 1.07, 1]
          }}
          transition={{ duration: 18, repeat: Infinity, repeatType: "loop", ease: "linear" }}
        >
          <FaLaptopCode />
        </motion.div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 z-10 py-20">
        <motion.div 
          ref={ref}
          className="max-w-4xl mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          <motion.div 
            className="relative h-auto md:h-24 mb-10"
            variants={itemVariants}
          >
            <motion.h1 
            className="block sm:inline-block 
            text-5xl md:text-6xl lg:text-7xl 
            font-bold text-white 
            leading-tight md:leading-snug"
            >
            <AnimatePresence mode="wait">
  <motion.div
    key={currentTextIndex}
    className={`${textOptions[currentTextIndex].color} font-extrabold`}
    variants={textVariants}
    initial="hidden"
    animate="visible"
    exit="exit"
  >
    {(() => {
      // 1️⃣ on découpe en 1er mot + reste
      const [firstWord, ...rest] = textOptions[currentTextIndex].text.split(' ');
      const secondPart = rest.join(' ');

      // 2️⃣ helper pour animer chaque lettre
      const mapLetters = (word, offset = 0) =>
        word.split('').map((letter, i) => (
          <motion.span
            key={offset + i}
            custom={offset + i}
            variants={letterVariants}
            initial="hidden"
            animate="visible"
            style={{
              display: 'inline-block',
              textShadow: '0 0 15px rgba(255,255,255,0.3)'
            }}
          >
            {letter === ' ' ? '\u00A0' : letter}
          </motion.span>
        ));

      return (
        <>
          {/* première partie */}
          {mapLetters(firstWord)}

          {/* espace insécable sur desktop (≥640px) */}
          <span className="hidden sm:inline">&nbsp;</span>

          {/* saut de ligne sur mobile (<640px) */}
          <br className="block sm:hidden" />

          {/* deuxième partie */}
          {mapLetters(secondPart, firstWord.length)}
        </>
      );
    })()}
  </motion.div>
</AnimatePresence>


              <span className="ml-3 block mt-2 mb-10 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">sur mesure</span>
            </motion.h1>
          </motion.div>
          
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-10 text-white mt-16"
            variants={itemVariants}
          >
            pour votre entreprise
          </motion.h2>
          
          <motion.p 
            className="text-xl md:text-2xl mb-10 text-blue-100"
            variants={itemVariants}
          >
            Des sites web modernes, rapides et optimisés pour convertir vos visiteurs en clients.
          </motion.p>
          
          <motion.div 
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <AnimatedButton 
              to="/Expertise" 
              type="secondary"
              size="lg"
              className="w-full sm:w-auto px-8 py-4 text-xl shadow-lg"
            >
              <div className="flex items-center justify-center">
                <FaRocket className="mr-2" />
                Expertise gratuite
              </div>
            </AnimatedButton>
            
            <AnimatedButton 
              to="/services" 
              type="outline"
              size="lg"
              className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-primary px-8 py-4 text-xl group"
            >
              <div className="flex items-center justify-center">
                <FaLaptopCode className="mr-2 group-hover:text-primary" />
                <span className="group-hover:text-primary">Découvrir nos services</span>
              </div>
            </AnimatedButton>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <motion.div 
          className="w-8 h-14 border-2 border-white rounded-full flex justify-center p-1"
          animate={{ y: [0, 10, 0] }}
          transition={{ 
            duration: 1.5, 
            repeat: Infinity,
            repeatType: "loop" 
          }}
        >
          <motion.div 
            className="w-1.5 h-3 bg-white rounded-full"
            animate={{ y: [0, 10, 0] }}
            transition={{ 
              duration: 1.5, 
              repeat: Infinity,
              repeatType: "loop" 
            }}
          />
        </motion.div>
      </motion.div>
      
      {/* Diagonal divider */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 100%, 100% 0)' }}></div>
    </section>
  );
};

export default HeroSection;